//----------------------------------------
// カメラフォーカス設定
//----------------------------------------
// スキャン時のフォーカスの当て方を保持する。
// 優先順位・保存方法・本番での固定は ScanSDK切替（scanSdkSetting.js）と同じ方式に揃えている。
// 優先順位： URLパラメータ(?focus=) > localStorage > 既定値(NEAR)
//
// ※ネイティブのデモアプリでは barkoder が短焦点側に張り付いており、
// 　手元で距離を合わせるだけで読めるため速いという指摘があった。
// 　Web版でも MediaStreamTrack の制約でフォーカスを固定できる（詳細は scanHost.js の applyCameraFocus）。
//
// ※NEAR は単焦点固定（focusDistance を最も近い側＝range.min へ寄せる）。
// 　focusDistance の単位はメートル。詳細は scanHost.js の applyCameraFocus のコメントを参照。
// 　距離を数値で選ぶこともできる。WebViewからはURLパラメータを付けられないため、
// 　設定画面から選べる必要がある。
// 　※端末の range より小さい値を選んだ場合は min に丸められる。
// 　　たとえば min が 0.1 の端末で 0.08 を選ぶと 0.1 が指定され、NEAR と同じ動作になる。

export const CAMERA_FOCUS_AUTO = "AUTO";
export const CAMERA_FOCUS_NEAR = "NEAR";

//focusDistance の指定値（単位はメートル
//※実機での総当たりにより、単位はメートル（W3C仕様どおり）と確定した。
//　DIGNO BX3 では 0.08（8cm）が最も良好で、0.2（20cm）も可、5（5m）は認識しなかった。
//　バーコードをかざす距離は近いので、近距離側を細かく用意してある。
//　範囲外の値は呼び出し側で min〜max に丸めるため、端末をまたいでも安全。
//　増減させたい場合はここを変えるだけでよい
export const CAMERA_FOCUS_VALUES = ["0.1", "0.15", "0.2", "0.25"];

//選択肢（設定画面の並び順
export const CAMERA_FOCUS_LIST = [
	CAMERA_FOCUS_AUTO,
	CAMERA_FOCUS_NEAR,
	...CAMERA_FOCUS_VALUES,
];

//設定画面の表示名
//※NEAR は focusDistance の min（その端末が寄れる限界）
//※単位はメートルなので cm に直して併記する
export function getCameraFocusLabel(focus) {
	if (focus === CAMERA_FOCUS_AUTO) return "AUTO（SDKに任せる）";
	if (focus === CAMERA_FOCUS_NEAR) return "NEAR（最短）";

	const num = Number(focus);
	return Number.isFinite(num) ? focus + "（" + Math.round(num * 100) + "cm）" : focus;
}

//既定
//※実機（DIGNO BX3）で最短固定が最も良好だったため NEAR を既定にしている。
//　AUTO（SDKに任せる）が従来の挙動なので、問題が出たらここを戻す
export const CAMERA_FOCUS_DEFAULT = CAMERA_FOCUS_NEAR;

//localStorageキー
const STORAGE_KEY = "regigo_camera_focus";

//本番ドメイン（フォーカスは選択制にせず、envの指定で固定する
const FIXED_FOCUS_DOMAIN = "miniapp.regigo.jp";

//フォーカス名として正しいか
//※URLパラメータやenvは大文字小文字を問わない（?focus=near でも通す
//※数値（?focus=0.3 など）はそのまま focusDistance として扱う。
//　NEAR（＝その端末が寄れる限界）では距離が合わない場合に、実機で値を詰められるようにしてある
function normalize(value) {
	if (typeof value !== "string") {
		return null;
	}

	const trimmed = value.trim();
	if (trimmed === "") {
		return null;
	}

	const upper = trimmed.toUpperCase();
	if (CAMERA_FOCUS_LIST.indexOf(upper) !== -1) {
		return upper;
	}

	//数値指定
	const num = Number(trimmed);
	return Number.isFinite(num) && num >= 0 ? trimmed : null;
}

//----------------------------------------
//指定された focusDistance の取り出し
//----------------------------------------
// focusDistance へそのまま渡す値を返す。単位はメートル。
// ・数値（"0.08" や ?focus=0.08）→ そのまま
// ・AUTO / NEAR                  → null（呼び出し側で range から決める）
//
// 範囲外の値は呼び出し側で range に丸める。

export function getExplicitFocusDistance(focus) {
	if (typeof focus !== "string") return null;

	const upper = focus.toUpperCase();
	if (upper === CAMERA_FOCUS_AUTO || upper === CAMERA_FOCUS_NEAR) return null;

	const num = Number(focus);
	return Number.isFinite(num) && num >= 0 ? num : null;
}

//----------------------------------------
//フォーカス固定（本番）かどうか
//----------------------------------------
// true の場合：
//   ・設定は REACT_APP_CAMERA_FOCUS で固定（URLパラメータ・localStorageは無視
//   ・設定画面のフォーカス選択は非表示

export function isCameraFocusFixed() {
	if (typeof window === "undefined") {
		return false;
	}
	return window.location.host === FIXED_FOCUS_DOMAIN;
}

//envで指定された設定（未設定・不正値は既定値
function getFixedCameraFocus() {
	const value = normalize(process.env.REACT_APP_CAMERA_FOCUS);
	if (value) {
		return value;
	}
	return CAMERA_FOCUS_DEFAULT;
}

//----------------------------------------
//設定取得
//----------------------------------------

export function getCameraFocus() {
	if (typeof window === "undefined") {
		return CAMERA_FOCUS_DEFAULT;
	}

	//本番はenvで固定（URLパラメータ・localStorageは参照しない
	if (isCameraFocusFixed()) {
		return getFixedCameraFocus();
	}

	//URLパラメータ（?focus=near など）優先：指定時はlocalStorageにも保存
	try {
		const param = normalize(new URLSearchParams(window.location.search).get("focus"));
		if (param) {
			saveCameraFocus(param);
			return param;
		}
	} catch (e) {
		console.log("getCameraFocus: urlParams error, " + e);
	}

	//localStorage
	try {
		const stored = normalize(window.localStorage.getItem(STORAGE_KEY));
		if (stored) {
			return stored;
		}
	} catch (e) {
		console.log("getCameraFocus: localStorage error, " + e);
	}

	return CAMERA_FOCUS_DEFAULT;
}

//----------------------------------------
//設定保存
//----------------------------------------

export function saveCameraFocus(focus) {
	//本番はenv固定のため保存しない
	if (isCameraFocusFixed()) {
		return getFixedCameraFocus();
	}

	const value = normalize(focus);
	if (!value) {
		return CAMERA_FOCUS_DEFAULT;
	}

	try {
		window.localStorage.setItem(STORAGE_KEY, value);
	} catch (e) {
		console.log("saveCameraFocus: localStorage error, " + e);
	}
	return value;
}
